import { TRPCLink, httpBatchLink } from "@trpc/client";
import { observable } from "@trpc/server/observable";
import { AnyRouter } from "@trpc/server";
import { tokenStorage } from "../../utils/auth/tokenStorage";
import config from "../../config/environment";
import {
  ExponentialBackoff,
  isRetryableError,
} from "../../utils/exponentialBackoff";

let isRefreshingToken = false;
let refreshSubscribers: Array<(token: string) => void> = [];

const subscribeTokenRefresh = (callback: (token: string) => void) => {
  refreshSubscribers.push(callback);
};

const onTokenRefreshed = (token: string) => {
  refreshSubscribers.forEach((callback) => callback(token));
  refreshSubscribers = [];
};

const refreshAuthToken = async (): Promise<string> => {
  // The refresh token normally travels in the HttpOnly cookie (sent via
  // credentials: "include"). A legacy token may still linger in JS storage from
  // before the cookie move (#41) — replay it once as a transition fallback.
  const legacyRefreshToken = tokenStorage.getRefreshToken();

  const backoff = new ExponentialBackoff({
    maxRetries: 3,
    initialDelay: 1000,
    maxDelay: 10000,
  });

  let lastError: any;

  while (!backoff.isExhausted()) {
    try {
      const response = await fetch(`${config.api.url}/trpc/auth.refreshToken`, {
        method: "POST",
        credentials: "include", // send the HttpOnly refresh cookie
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          json: legacyRefreshToken ? { refreshToken: legacyRefreshToken } : {},
        }),
      });

      if (!response.ok) {
        const error = new Error("Failed to refresh token");
        (error as any).response = { status: response.status };
        throw error;
      }

      const data = await response.json();
      const result = data.result?.data?.json;

      if (!result?.accessToken && !result?.token) {
        throw new Error("Invalid refresh response");
      }

      // Handle both possible response formats
      const newAccessToken = result.accessToken || result.token;

      tokenStorage.setAuthToken(newAccessToken);

      // The rotated refresh token is set by the server as an HttpOnly cookie.
      // Clear any legacy JS copy so future refreshes rely solely on the cookie.
      if (legacyRefreshToken) {
        tokenStorage.removeRefreshToken();
      }

      return newAccessToken;
    } catch (error) {
      lastError = error;

      // Don't retry on 401 (invalid refresh token)
      if ((error as any).response?.status === 401) {
        throw error;
      }

      // Check if error is retryable
      if (!isRetryableError(error)) {
        throw error;
      }

      if (!backoff.isExhausted()) {
        const delay = backoff.getNextDelay();
        console.log(
          `Retrying token refresh after ${delay}ms (attempt ${backoff.getAttempt()})`,
        );
        await new Promise((resolve) => setTimeout(resolve, delay));
      }
    }
  }

  throw lastError || new Error("Failed to refresh token after retries");
};

export const authRefreshLink =
  <TRouter extends AnyRouter>(): TRPCLink<TRouter> =>
  () => {
    return ({ next, op }) => {
      return observable((observer) => {
        let retryCount = 0;
        const maxRetries = 1;

        const executeOperation = (token?: string) => {
          const headers: Record<string, string> = {
            ...(typeof op.context?.headers === "object" &&
            op.context?.headers !== null
              ? op.context.headers
              : {}),
          };

          if (token) {
            headers.Authorization = `Bearer ${token}`;
          } else {
            const authToken = tokenStorage.getAuthToken();
            if (authToken) {
              headers.Authorization = `Bearer ${authToken}`;
            }
          }

          const subscription = next({
            ...op,
            context: {
              ...op.context,
              headers,
            },
          }).subscribe({
            next(value) {
              observer.next(value);
            },
            error(err) {
              const httpStatus = err?.data?.httpStatus;

              if (
                httpStatus === 401 &&
                retryCount < maxRetries &&
                tokenStorage.hasSession() &&
                !op.path.includes("auth.refreshToken")
              ) {
                retryCount++;

                if (!isRefreshingToken) {
                  isRefreshingToken = true;

                  refreshAuthToken()
                    .then((newToken) => {
                      onTokenRefreshed(newToken);
                      executeOperation(newToken);
                    })
                    .catch((refreshError) => {
                      tokenStorage.clearTokens();
                      window.dispatchEvent(new CustomEvent("auth:expired"));
                      observer.error(refreshError);
                    })
                    .finally(() => {
                      isRefreshingToken = false;
                    });
                } else {
                  subscribeTokenRefresh((newToken) => {
                    executeOperation(newToken);
                  });
                }
              } else {
                observer.error(err);
              }
            },
            complete() {
              observer.complete();
            },
          });

          return () => {
            subscription.unsubscribe();
          };
        };

        return executeOperation();
      });
    };
  };

export function createTrpcLinksWithRefresh() {
  return [
    authRefreshLink(),
    httpBatchLink({
      url: `${config.api.url}${config.api.trpcEndpoint}`,
      fetch(url, options) {
        return fetch(url, {
          ...options,
          credentials: "include",
        });
      },
      headers: () => {
        const headers: Record<string, string> = {};
        const token = tokenStorage.getAuthToken();
        if (token) {
          headers.Authorization = `Bearer ${token}`;
        }
        return headers;
      },
    }),
  ];
}
